import * as R from 'ramda';
import { normalize, schema } from 'normalizr';
import * as Sentry from '@sentry/react';
import sportsApi from '@app/utils/sportsApi';
import i18n from '@app/i18n/config';

const EIGHT_HOURS = 288e5;

const clientsSchema = [new schema.Entity('clients')];

const login = store => async state => {
    try {
        let error = null;

        // Clear stale auth data from localStorage
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('xat');

        const userRequest = await sportsApi
            .url('/users/me')
            .options({
                credentials: 'include',
                context: { ssoBootstrap: true },
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                },
            })
            .get()
            .badRequest(e => (error = e))
            .unauthorized(e => (error = e));

        const { headers, status } = await userRequest.res();

        if (error) {
            Sentry.captureException({
                category: 'API',
                message: 'SSO login error: ' + error.message,
                data: {
                    responseCode: error.status,
                },
            });
            return R.assocPath(
                ['user', 'error'],
                {
                    message: error.message,
                    status: error.status,
                },
                state,
            );
        }

        let token;
        try {
            token = headers.get('x-auth-token');
            if (typeof token !== 'string' || !token.trim()) {
                throw new Error(i18n.t('errors.generic'));
            }
        } catch {
            Sentry.captureException({
                category: 'API',
                message: 'SSO login error: invalid response auth token',
                data: { responseCode: status },
            });
            throw new Error(i18n.t('errors.generic'));
        }

        // Retrieve payload (user information)
        const user = await userRequest.json();
        const clients = R.path(['clients'], user) || [];

        if (!clients.length) {
            Sentry.captureException({
                category: 'API',
                message:
                    'SSO login error: no clients associated to user account',
                data: {
                    user: user,
                },
            });

            // Update store with user info and error message
            store.setState({
                user: {
                    ...store.getState().user,
                    ...user,
                    token,
                    clients,
                    clientsById: {},
                    selectedClientId: null,
                    selectedUserClientId: null,
                    isSelectingClient: false,
                    isLoggedIn: false,
                    isLoading: false,
                    error: 'You do not have a client associated to your account',
                    ssoLogin: true,
                },
            });

            return;
        }

        // Generate a timestamp to store token validation time
        user.timestamp = Date.now() + EIGHT_HOURS;
        user.token = token;
        user.isSelectingClient = true;

        const normalizedClients = normalize(user.clients, clientsSchema);
        user.clientsById = normalizedClients.entities.clients;

        store.setState({
            user: {
                ...store.getState().user,
                ...user,
                selectedClientId: null,
                selectedUserClientId: null,
                isLoggedIn: false,
                isLoading: false,
                error: null,
                ssoLogin: true,
            },
        });

        Sentry.addBreadcrumb({
            category: 'API',
            message: 'SSO login',
            data: {
                responseCode: userRequest.status,
                user: user,
            },
        });
    } catch (e) {
        store.setState({
            user: {
                isLoading: false,
                error: {
                    message: e.message,
                    ssoError: true,
                },
            },
        });

        Sentry.addBreadcrumb({
            category: 'API',
            message: 'SSO login error: ' + e.message,
            data: {
                responseCode: e.status,
            },
        });
        // We need to re-throw the error to be caught by the ErrorBoundary
        throw e;
    }
};

export default login;
